import { useRef, useState } from 'react';
import { cropPageImage, type RenderedPage } from '@/utils/pdfCapture';

interface PdfCaptureViewerProps {
  pages: RenderedPage[];
  onCapture: (dataUrl: string, pageIndex: number) => void;
}

interface DragState {
  pageIndex: number;
  startX: number;
  startY: number;
  curX: number;
  curY: number;
  rectLeft: number;
  rectTop: number;
  scaleX: number;
  scaleY: number;
}

const ZOOM_LEVELS = [1, 1.5, 2];
/** 이보다 작게 드래그하면 캡처로 보지 않음 (원본 픽셀 기준) */
const MIN_SIZE = 12;

export default function PdfCaptureViewer({ pages, onCapture }: PdfCaptureViewerProps) {
  const [zoom, setZoom] = useState(1);
  const [captureMode, setCaptureMode] = useState(true);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);

  const beginDrag = (e: React.PointerEvent<HTMLImageElement>, page: RenderedPage) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const scaleX = page.width / rect.width;
    const scaleY = page.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const state: DragState = {
      pageIndex: page.index,
      startX: x,
      startY: y,
      curX: x,
      curY: y,
      rectLeft: rect.left,
      rectTop: rect.top,
      scaleX,
      scaleY,
    };
    dragRef.current = state;
    setDrag(state);
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const moveDrag = (e: React.PointerEvent<HTMLImageElement>) => {
    const cur = dragRef.current;
    if (!cur) return;
    const next: DragState = {
      ...cur,
      curX: (e.clientX - cur.rectLeft) * cur.scaleX,
      curY: (e.clientY - cur.rectTop) * cur.scaleY,
    };
    dragRef.current = next;
    setDrag(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLImageElement>, page: RenderedPage) => {
    const cur = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!cur) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const x = Math.min(cur.startX, cur.curX);
    const y = Math.min(cur.startY, cur.curY);
    const w = Math.abs(cur.curX - cur.startX);
    const h = Math.abs(cur.curY - cur.startY);
    if (w < MIN_SIZE || h < MIN_SIZE) return;
    const dataUrl = cropPageImage(page.canvas, { x, y, width: w, height: h });
    if (dataUrl) onCapture(dataUrl, page.index);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* 툴바 */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-crop-2-line text-base"></i>
          </span>
          <div className="leading-tight">
            <p className="text-sm font-bold text-foreground-900">문제 영역 캡처</p>
            <p className="text-[11px] text-foreground-500">
              캡처할 문제를 손가락(마우스)으로 쭉 끌어서 사각형을 그리세요
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCaptureMode((v) => !v)}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition ${
              captureMode
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-300 bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            <i className={captureMode ? 'ri-crop-2-line text-sm' : 'ri-drag-move-2-line text-sm'}></i>
            {captureMode ? '캡처 모드 켜짐' : '캡처 모드 꺼짐'}
          </button>
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
            <span className="pl-2 pr-1 text-[11px] font-semibold text-foreground-500">확대</span>
            {ZOOM_LEVELS.map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => setZoom(z)}
                className={`min-w-[40px] cursor-pointer whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold transition ${
                  zoom === z
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-200'
                }`}
              >
                {z}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 페이지 */}
      <div className="max-h-[72vh] overflow-auto rounded-lg border border-background-200 bg-background-100 p-3">
        {pages.map((page) => {
          const box =
            drag && drag.pageIndex === page.index
              ? {
                  left: Math.min(drag.startX, drag.curX) / drag.scaleX,
                  top: Math.min(drag.startY, drag.curY) / drag.scaleY,
                  width: Math.abs(drag.curX - drag.startX) / drag.scaleX,
                  height: Math.abs(drag.curY - drag.startY) / drag.scaleY,
                }
              : null;

          return (
            <div
              key={page.index}
              className="relative mx-auto mb-4 last:mb-0"
              style={{ width: `${zoom * 100}%` }}
            >
              <img
                src={page.dataUrl}
                alt={`PDF ${page.index + 1}페이지`}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                onPointerDown={captureMode ? (e) => beginDrag(e, page) : undefined}
                onPointerMove={captureMode ? moveDrag : undefined}
                onPointerUp={captureMode ? (e) => endDrag(e, page) : undefined}
                onPointerCancel={
                  captureMode
                    ? () => {
                        dragRef.current = null;
                        setDrag(null);
                      }
                    : undefined
                }
                style={{ touchAction: captureMode ? 'none' : 'auto' }}
                className={`block w-full select-none rounded border border-background-300 bg-white ${
                  captureMode ? 'cursor-crosshair' : 'cursor-grab'
                }`}
              />
              <span className="pointer-events-none absolute left-2 top-2 rounded bg-foreground-900/70 px-1.5 py-0.5 text-[10px] font-semibold text-background-50">
                p.{page.index + 1}
              </span>
              {box && box.width > 1 && box.height > 1 && (
                <div
                  className="pointer-events-none absolute border-2 border-primary-500 bg-primary-500/20"
                  style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
                />
              )}
            </div>
          );
        })}
      </div>

      <p className="flex items-center gap-1.5 text-[11px] text-foreground-500">
        <i className="ri-information-line text-sm text-secondary-600"></i>
        {captureMode
          ? '사각형을 놓는 순간 캡처된 이미지가 오답노트에 자동으로 추가됩니다.'
          : '캡처 모드가 꺼져 있어요. 화면을 위아래로 넘겨 페이지를 볼 수 있고, 캡처하려면 캡처 모드를 켜세요.'}
      </p>
    </div>
  );
}